import { useEffect, useState } from "react";
import { useAction } from "convex/react";
import { anyApi } from "convex/server";
import { Dialog } from "./Dialog";
import { Field, inputCls } from "./form-controls";

// Adds a tenant to a lease. Server-side `tenancies.inviteToLease`
// handles three identifiers:
//
// 1. Email maps to existing Convex user → attach immediately.
// 2. Email is new → Clerk's /v1/invitations sends a setup email.
// 3. Phone number → record a pending invite; tenant signs up via
//    Clerk's phone-OTP flow; upsertCurrent matches on phoneNumber.
//
// At least one of email/phoneNumber is required (both are fine).
// Email-only and phone-only and both-together all work.
export function AddTenantDialog({
  open,
  onClose,
  leaseId,
  unitLabel,
}: {
  open: boolean;
  onClose: () => void;
  leaseId: string;
  unitLabel?: string;
}) {
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const invite = useAction(anyApi.tenancies.inviteToLease);

  useEffect(() => {
    if (!open) return;
    setEmail("");
    setPhoneNumber("");
    setFirstName("");
    setLastName("");
    setStartDate("");
    setError(null);
    setSuccessMsg(null);
  }, [open, leaseId]);

  const trimmedEmail = email.trim();
  const trimmedPhone = phoneNumber.trim();
  const canSubmit = !!(trimmedEmail || trimmedPhone) && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const result = (await invite({
        leaseId,
        email: trimmedEmail || undefined,
        phoneNumber: trimmedPhone || undefined,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        startDate: startDate || undefined,
      })) as { invited: boolean; alreadyExisted: boolean };

      const label = trimmedEmail || trimmedPhone;
      if (result.alreadyExisted) {
        setSuccessMsg("Already on this lease — no change.");
      } else if (result.invited && trimmedEmail) {
        setSuccessMsg(
          `Invited ${trimmedEmail}. Clerk just emailed them a link to finish setup.`,
        );
      } else if (result.invited) {
        setSuccessMsg(
          `Saved invite for ${trimmedPhone}. Share the maisons URL with them — they'll sign up by phone and the tenancy will attach automatically.`,
        );
      } else {
        setSuccessMsg(`Added ${label} to the lease.`);
      }
      setEmail("");
      setPhoneNumber("");
      setFirstName("");
      setLastName("");
      setStartDate("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={unitLabel ? `Add tenant to ${unitLabel} lease` : "Add tenant"}
    >
      <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
        <Field label="Tenant email">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            placeholder="tenant@example.com"
            className={inputCls}
          />
        </Field>
        <Field label="Tenant phone">
          <input
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="+15035551234"
            className={inputCls}
          />
          <p className="mt-1 text-xs text-slate-500">
            E.164 format preferred (US 10-digit numbers also fine). At
            least one of email or phone is required — provide both if
            the tenant has both.
          </p>
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="First name">
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Jane"
              className={inputCls}
            />
          </Field>
          <Field label="Last name">
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Doe"
              className={inputCls}
            />
          </Field>
        </div>
        <Field label="Start date on this lease">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className={inputCls}
          />
        </Field>
        <p className="text-xs text-slate-500">
          Email invites get an automatic setup link from Clerk. Phone
          invites are recorded silently — share the maisons URL with
          your tenant; the tenancy attaches when they sign up by phone.
        </p>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {successMsg && (
          <p className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-700">
            {successMsg}
          </p>
        )}
        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="submit"
            disabled={!canSubmit}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            {submitting ? "Working…" : "Add tenant"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
